import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  Button,
  Switch,
  Avatar,
  FormControlLabel,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  Checkbox,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { API_ENDPOINTS } from '../../config/apiconfig';
import { useLocation } from 'react-router-dom';
import api from '../../api/axiosInstance';
import { settingsApi } from '../../api/settingsApi';
import { challanApi } from '../../api/challanApi';
import LeadProviderPanel from './LeadProviderPanel';
import {
  Business as BusinessIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  Draw as SignatureIcon,
  Receipt as InvoiceIcon,
  Share as ShareIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import "./SettingsList.css";

const SettingsList = () => {
  const location = useLocation();

  // API responses can contain booleans as true/false, 1/0, or strings.
  // Normalizing them prevents a value such as "false" from being treated
  // as truthy by React Switch checked={...}.
  const toBoolean = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      return ["true", "1", "yes", "on"].includes(value.trim().toLowerCase());
    }
    return false;
  };

  const AccountingIntegrationIcon = () => (
    <svg
      width="48"
      height="48"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="8"
        y="6"
        width="28"
        height="50"
        rx="4"
        stroke="#4F46E5"
        strokeWidth="2.8"
      />

      <line
        x1="14"
        y1="18"
        x2="30"
        y2="18"
        stroke="#4F46E5"
        strokeWidth="2.5"
        strokeLinecap="round"
      />

      <line
        x1="14"
        y1="27"
        x2="30"
        y2="27"
        stroke="#4F46E5"
        strokeWidth="2.5"
        strokeLinecap="round"
      />

      <line
        x1="14"
        y1="36"
        x2="24"
        y2="36"
        stroke="#4F46E5"
        strokeWidth="2.5"
        strokeLinecap="round"
      />

      <text
        x="41"
        y="25"
        fontSize="18"
        fontWeight="700"
        fill="var(--settings-success)"
        fontFamily="Arial"
      >
        
      </text>

      <path
        d="M44 36a10 10 0 1 1-1 20"
        stroke="var(--settings-success)"
        strokeWidth="2.8"
        fill="none"
        strokeLinecap="round"
      />

      <path
        d="M42 34l5 2l-2 5"
        stroke="var(--settings-success)"
        strokeWidth="2.8"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path
        d="M35 54l-5-2l2-5"
        stroke="var(--settings-success)"
        strokeWidth="2.8"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  // Tab name to index mapping
  const tabMap = useMemo(() => ({
    'organization': 0,
    'banking': 1,
    'invoice': 2,
    'sharing': 3,
    'branding': 4,
    'accounting': 5,
  }), []);

  // ===== STATE DECLARATIONS =====
  
  // Tab State
  const [tabValue, setTabValue] = useState(0);

  // Dialog State
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);

  // Organization Profile State
  const [orgProfile, setOrgProfile] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    company: "",
  });
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgError, setOrgError] = useState("");
  const [orgFieldErrors, setOrgFieldErrors] = useState({ email: "", phone: "" });

  // Invoice Settings State
  const [invoiceSettings, setInvoiceSettings] = useState({
    invoicePrefix: "",
    companyName: "",
    companyAddress: "",
    companyState: "",
    companyStateCode: "",
    gstin: "",
    cin: "",
    pan: "",
    hsnSacCode: "",
    phoneNumber: "",
    website: "",
    email: "",
    challanNumberFormat: "",
    challanSequence: "",
    terms: "",
    defaultFormat: false,
    useEmailShare: false,
    useManualChallan: false,
    manualChallanNumber: "",
    useWhatsAppShare: false,
    enableChallanImageUpload: false,
    enableChallanGeoLocation: false,
    showInvoicePopupAfterChallan: true,
    // WhatsApp Marketing Templates
    whatsappNewCustomerTemplate: "new_customer",
    whatsappProductArrivalTemplate: "product_arrival",
    whatsappSpecialOfferTemplate: "special_offer",
    whatsappNewCustomerPdfUrl: "",
    // Product Arrival
    whatsappProductArrivalImageUrl: "",
    whatsappProductArrivalCompanyName: "",
    // Special Offer
    whatsappSpecialOfferImageUrl: "",
    whatsappSpecialOfferDiscount: "",
    whatsappSpecialOfferExpiryDate: "",
    whatsappSpecialOfferCompanyName: "",
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [newCustomerPdfFile, setNewCustomerPdfFile] = useState(null);
  const [newCustomerPdfPreview, setNewCustomerPdfPreview] = useState(null);

  // Manual Challan Number Validation State
  const [manualChallanChecking, setManualChallanChecking] = useState(false);
  const [manualChallanError, setManualChallanError] = useState("");
  const [manualChallanValid, setManualChallanValid] = useState(null);
  // Each tab gets its own independent edit-mode toggle. Previously
  // Organization/Sharing/Accounting shared one `isEditMode` flag and
  // Invoice/Branding shared one `isInvoiceEditMode` flag, so opening
  // "Edit" on one tab silently unlocked and re-locked unrelated tabs.
  const [isEditMode, setIsEditMode] = useState(false); // Organization tab
  const [isInvoiceEditMode, setIsInvoiceEditMode] = useState(false); // Invoice Settings tab
  const [isSharingEditMode, setIsSharingEditMode] = useState(false); // Sharing Settings tab
  const [isBrandingEditMode, setIsBrandingEditMode] = useState(false); // Branding & Documents tab
  const [isAccountingEditMode, setIsAccountingEditMode] = useState(false); // Accounting Integration tab
  const [isBankingEditMode, setIsBankingEditMode] = useState(false); // Banking Details tab
  const [showSuccess, setShowSuccess] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [qrImageFile, setQrImageFile] = useState(null);

  // Banking Details State
  const [bankingSettings, setBankingSettings] = useState({
    accountName: "",
    accountNumber: "",
    ifscCode: "",
    branch: "",
    bankName: "",
    upiId: "",
  });
  const [qrImagePreview, setQrImagePreview] = useState(null);

  // Accounting Integration State
  const [accountingSettings, setAccountingSettings] = useState(() => {
    let saved = {};

    try {
      saved = JSON.parse(
        localStorage.getItem("ctms_accounting_settings") || "{}"
      );
    } catch {
      saved = {};
    }

    return {
      provider: saved.provider || "TallyPrime",
      enabled: Boolean(saved.enabled),
      host: saved.host || "localhost",
      port: saved.port || "9000",
      companyName: saved.companyName || "",
      invoiceCreationMode: saved.invoiceCreationMode || "Ask",
      voucherType: saved.voucherType || "Sales",
      salesLedger: saved.salesLedger || "Sales Account",
      defaultNarration:
        saved.defaultNarration || "Invoice Against Delivered Challan",
      autoCreateCustomerLedger:
        saved.autoCreateCustomerLedger ?? true,
      autoCreateStockItem:
        saved.autoCreateStockItem ?? true,
      saveVoucherNumber:
        saved.saveVoucherNumber ?? true,
    };
  });

  // Accounting integration is controlled by the switch itself.
  // Persist locally because the supplied project does not expose an
  // accounting-settings API in SettingsList.
  const accountingConnected = accountingSettings.enabled;
  // Lead providers (IndiaMART / TradeIndia) are configured through
  // LeadProviderPanel instead of the TallyPrime accounting fields.
  const isLeadProvider = ['IndiaMART', 'TradeIndia'].includes(accountingSettings.provider);

  useEffect(() => {
    try {
      localStorage.setItem(
        "ctms_accounting_settings",
        JSON.stringify(accountingSettings)
      );
    } catch {
      // Ignore storage failures; UI state still works.
    }
  }, [accountingSettings]);

  // ===== USE EFFECT HOOKS =====

  // Handle URL parameter for tab selection
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam && tabMap[tabParam] !== undefined) {
      setTabValue(tabMap[tabParam]);
    }
  }, [location, tabMap]);

  // Load Organization Profile on mount
  useEffect(() => {
    loadOrgProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== TOGGLE HANDLERS =====
  // Keep switches independent from the page Edit/Save mode.
  const handleInvoiceToggle = (field) => (event) => {
    const checked = event.target.checked;

    setInvoiceSettings((prev) => ({
      ...prev,
      [field]: checked,
    }));
  };

  const handleAccountingToggle = (event) => {
    const checked = event.target.checked;

    setAccountingSettings((prev) => ({
      ...prev,
      enabled: checked,
    }));
  };

  const handleTestConnection = async () => {
    try {
      const payload = {
        provider: accountingSettings.provider === 'TallyPrime' ? 'TALLY' : accountingSettings.provider.toUpperCase(),
        host: accountingSettings.host,
        port: parseInt(accountingSettings.port) || 9000,
        companyName: accountingSettings.companyName,
      };

      console.log("Test Connection Payload:", payload);
      const response = await api.post('/accounting/test-connection', payload);
      const data = response.data;

      console.log("Test Connection Response:", data);

      if (data.connected && data.status === 'SUCCESS') {
        toast.success(data.message || 'Successfully connected to Tally');
        setAccountingSettings(prev => ({ ...prev, enabled: true }));
      } else {
        toast.error(data.message || 'Failed to connect to Tally');
        setAccountingSettings(prev => ({ ...prev, enabled: false }));
      }
    } catch (err) {
      console.error("Test Connection Error:", err);
      const message = err.response?.data?.message || err.message || 'Connection test failed';
      toast.error(message);
      setAccountingSettings(prev => ({ ...prev, enabled: false }));
    }
  };

  // ===== API FUNCTIONS =====

  const loadOrgProfile = async () => {
    try {
      setOrgLoading(true);
      const [res, settingsRes] = await Promise.all([
        api.get(`${API_ENDPOINTS.PROFILE.GET}/getById/1`),
        settingsApi.getSettings().catch(() => null),
      ]);
      console.log("Organization Profile API Response:", res.data);
      const data = res.data || {};
      const settingsData =
        settingsRes && settingsRes.status !== 204 ? (settingsRes.data || {}) : {};
      // Business Name / Email / Mobile / Address are common with the Company &
      // GST section; fall back to saved settings when the profile API leaves
      // them empty so existing values are never stranded.
      setOrgProfile({
        ...data,
        name: data.name || settingsData.companyName || "",
        email: data.email || settingsData.email || "",
        phone: normalizeOrgPhone(data.phone || settingsData.phoneNumber || ""),
        address: data.address || settingsData.companyAddress || "",
      });

      // Check if logo is in organization profile
      if (data.logo) {
        const logoData = data.logo.startsWith('data:') ? data.logo : `data:image/jpeg;base64,${data.logo}`;
        console.log("Logo found in organization profile:", logoData.substring(0, 100) + "...");
        setLogoPreview(logoData);
      }
      
      setOrgError("");
    } catch (err) {
      console.error("Error fetching organization profile:", err);
      if (err.response?.status === 401) {
        setOrgError("Authentication failed. Please login again.");
      } else {
        setOrgError("Failed to load organization data");
      }
    } finally {
      setOrgLoading(false);
    }
  };

  // Load Invoice Settings
  const loadInvoiceSettings = useCallback(async () => {
    try {
      const res = await settingsApi.getSettings();

      if (res.status === 204) return;

      const data = res.data;

      console.log("Settings API Response:", data);
      console.log("Logo data from API:", data.logo);
      console.log("Signature data from API:", data.signature);

      setInvoiceSettings({
        invoicePrefix: data.invoicePrefix || "",
        companyName: data.companyName || "",
        companyAddress: data.companyAddress || "",
        companyState: data.companyState || "",
        companyStateCode: data.companyStateCode || "",
        gstin: data.gstin || "",
        cin: data.cin || "",
        pan: data.pan || "",
        hsnSacCode: data.hsnSacCode || "",
        phoneNumber: data.phoneNumber || "",
        website: data.website || "",
        email: data.email || "",
        challanNumberFormat: data.challanNumberFormat || "",
        challanSequence: data.challanSequence || "",
        terms: data.termsAndConditions || "",
        defaultFormat: toBoolean(data.defaultFormat),
        useEmailShare: toBoolean(data.useEmailShare),
        useManualChallan: toBoolean(data.useManualChallan),
        manualChallanNumber: data.manualChallanStart || "",
        useWhatsAppShare: toBoolean(data.useWhatsAppShare),
        enableChallanImageUpload: toBoolean(data.enableChallanImageUpload),
        enableChallanGeoLocation: toBoolean(data.enableChallanGeoLocation),
        showInvoicePopupAfterChallan: toBoolean(data.showInvoicePopupAfterChallan),
        // WhatsApp Marketing Templates
        whatsappNewCustomerTemplate: data.whatsappNewCustomerTemplate || "new_customer",
        whatsappProductArrivalTemplate: data.whatsappProductArrivalTemplate || "product_arrival",
        whatsappSpecialOfferTemplate: data.whatsappSpecialOfferTemplate || "special_offer",
        whatsappNewCustomerPdfUrl: data.whatsappNewCustomerPdfUrl || "",
        // Product Arrival
        whatsappProductArrivalImageUrl: data.whatsappProductArrivalImageUrl || "",
        whatsappProductArrivalCompanyName: data.whatsappProductArrivalCompanyName || "",
        // Special Offer
        whatsappSpecialOfferImageUrl: data.whatsappSpecialOfferImageUrl || "",
        whatsappSpecialOfferDiscount: data.whatsappSpecialOfferDiscount || "",
        whatsappSpecialOfferExpiryDate: data.whatsappSpecialOfferExpiryDate || "",
        whatsappSpecialOfferCompanyName: data.whatsappSpecialOfferCompanyName || "",
      });

      if (data.logoBase64) {
        const logoData = data.logoBase64.startsWith('data:') ? data.logoBase64 : `data:image/jpeg;base64,${data.logoBase64}`;
        console.log("Processed logo data:", logoData.substring(0, 100) + "...");
        setLogoPreview(logoData);
      } else {
        console.log("No logo data found in API response");
      }

      if (data.signatureBase64) {
        const signatureData = data.signatureBase64.startsWith('data:') ? data.signatureBase64 : `data:image/png;base64,${data.signatureBase64}`;
        console.log("Processed signature data:", signatureData.substring(0, 100) + "...");
        setSignaturePreview(signatureData);
      } else {
        console.log("No signature data found in API response");
      }

    } catch (err) {
      console.error("Failed to load invoice settings", err);
    }
  }, []);

  // Load Banking Settings
  const loadBankingSettings = useCallback(async () => {
    try {
      const res = await settingsApi.getSettings();
      if (res.status === 204) return;
      const data = res.data;
      console.log("Banking Settings API Response:", data);

      setBankingSettings({
        accountName: data.accountName || "",
        accountNumber: data.accountNumber || "",
        ifscCode: data.ifscCode || "",
        branch: data.branch || "",
        bankName: data.bankName || "",
        upiId: data.upiId || "",
      });

      if (data.qrImageBase64) {
        const qrData = data.qrImageBase64.startsWith('data:') ? data.qrImageBase64 : `data:image/jpeg;base64,${data.qrImageBase64}`;
        console.log("Processed QR image data:", qrData.substring(0, 100) + "...");
        setQrImagePreview(qrData);
      } else {
        console.log("No QR image data found in API response");
      }
    } catch (err) {
      console.error("Failed to load banking settings", err);
    }
  }, []);

  // Load the saved settings as soon as the page opens. Without this every tab
  // (Invoice, Sharing, Branding, Banking) rendered the initial state instead of the
  // values stored on the backend.
  useEffect(() => {
    loadInvoiceSettings();
    loadBankingSettings();
  }, [loadInvoiceSettings, loadBankingSettings]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearTimeout(window.manualChallanCheckTimeout);
    };
  }, []);

  // Re-load when the user switches tabs so the UI never shows values left over
  // from an earlier visit. Skipped while that tab is in edit mode so unsaved
  // changes are not thrown away. The first render is ignored because the mount
  // effect above already fetched.
  const lastLoadedTabRef = useRef(tabValue);
  useEffect(() => {
    if (lastLoadedTabRef.current === tabValue) return;
    lastLoadedTabRef.current = tabValue;
    if (isInvoiceEditMode || isSharingEditMode || isBrandingEditMode || isBankingEditMode) return;
    loadInvoiceSettings();
    loadBankingSettings();
  }, [tabValue, isInvoiceEditMode, isSharingEditMode, isBrandingEditMode, isBankingEditMode, loadInvoiceSettings, loadBankingSettings]);

  // Manual Challan Number Validation Functions
  const fetchNextAvailableManualChallanNumber = useCallback(async (preferredNumber) => {
    try {
      const response = await challanApi.getNextAvailableManualChallanNumber(preferredNumber);
      return response.data;
    } catch (error) {
      console.error("Error fetching next available manual challan number:", error);
      return null;
    }
  }, []);

  const checkManualChallanNumberAvailability = useCallback(async (challanNumber) => {
    if (!challanNumber || challanNumber.trim() === "") {
      setManualChallanValid(null);
      setManualChallanError("");
      return;
    }

    const trimmedNumber = challanNumber.trim();

    setManualChallanChecking(true);
    setManualChallanError("");
    setManualChallanValid(null);

    try {
      const response = await challanApi.checkChallanNumberAvailability(trimmedNumber);
      const isAvailable = response.data;
      if (isAvailable) {
        setManualChallanValid(true);
        return;
      }

      // Duplicate found — auto-increment to the next available number
      const nextAvailable = await fetchNextAvailableManualChallanNumber(trimmedNumber);
      if (nextAvailable && nextAvailable !== trimmedNumber) {
        setInvoiceSettings((prev) => ({ ...prev, manualChallanNumber: nextAvailable }));
        setManualChallanValid(true);
        setManualChallanError("");
        toast.info(`Manual challan number "${trimmedNumber}" is already in use. Auto-incremented to ${nextAvailable}.`);
      } else {
        setManualChallanValid(false);
        setManualChallanError("Challan number already exists.");
      }
    } catch (error) {
      console.error("Error checking manual challan number:", error);
      setManualChallanValid(null);
      setManualChallanError("Failed to verify challan number");
    } finally {
      setManualChallanChecking(false);
    }
  }, [fetchNextAvailableManualChallanNumber]);

  // Handle file uploads
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please upload image only");
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please upload image only");
      return;
    }
    setSignatureFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setSignaturePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleQrImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please upload image only");
      return;
    }
    setQrImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setQrImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleNewCustomerPdfUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      alert("Please upload PDF only");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert("File size must be less than 10MB");
      return;
    }
    setNewCustomerPdfFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setNewCustomerPdfPreview(reader.result);
      // Store the base64 in the settings for preview
      setInvoiceSettings((prev) => ({ ...prev, whatsappNewCustomerPdfUrl: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  // Upload WhatsApp template header images (Product Arrival / Special Offer).
  // Stored as a base64 data URL, same as the logo/signature/QR flows.
  const handleSharingImageUpload = (field) => async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    e.target.value = "";
    if (!file.type.startsWith("image/")) {
      alert("Please upload image only");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }
    try {
      const dataUrl = await fileToBase64(file);
      setInvoiceSettings((prev) => ({ ...prev, [field]: dataUrl }));
    } catch (err) {
      console.error("Failed to read image file:", err);
      alert("Failed to read image file. Please try again.");
    }
  };

  const handleSharingImageRemove = (field) => {
    setInvoiceSettings((prev) => ({ ...prev, [field]: "" }));
  };

  // Organization field validation (checked while typing, on blur, and on save)
  // Strips formatting from API values and removes a leading 91/0 so the
  // digits match the +91 prefix shown next to the input.
  const normalizeOrgPhone = (value) => {
    let digits = String(value || "").replace(/\D/g, "");
    if (digits.length > 10 && digits.startsWith("91")) digits = digits.slice(2);
    if (digits.length > 10 && digits.startsWith("0")) digits = digits.slice(1);
    return digits;
  };

  const validateOrgEmailField = (value, required = true) => {
    const email = (value || "").trim();
    if (!email) return required ? "Email is required" : "";
    return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/.test(email)
      ? ""
      : "Enter a valid email address (e.g., name@company.com)";
  };

  const validateOrgPhoneField = (value) => {
    const phone = normalizeOrgPhone(value);
    if (!phone) return "Mobile number is required";
    if (phone.length !== 10) return "Mobile number must be 10 digits";
    return "";
  };

  // Save button stays disabled until Email and Mobile Number hold valid values
  const orgFormIsValid =
    !validateOrgEmailField(orgProfile.email) &&
    !validateOrgPhoneField(orgProfile.phone);

  // Save Organization Profile
  const handleSaveOrganization = async () => {
    // Block saving an invalid email or mobile number
    const emailError = validateOrgEmailField(orgProfile.email);
    const phoneError = validateOrgPhoneField(orgProfile.phone);
    if (emailError || phoneError) {
      setOrgFieldErrors({ email: emailError, phone: phoneError });
      return;
    }
    setOrgFieldErrors({ email: "", phone: "" });

    try {
      setOrgError("");
      const payload = {
        ...orgProfile,
        email: (orgProfile.email || "").trim(),
        phone: normalizeOrgPhone(orgProfile.phone),
      };

      const response = await api.put(`${API_ENDPOINTS.PROFILE.GET}/updateById/1`, payload);
      const savedProfile = response.data && typeof response.data === "object"
        ? { ...payload, ...response.data }
        : payload;

      setOrgProfile(savedProfile);

      // Persist Company & GST Details (shown in this tab) to the settings API.
      // Business Name / Email / Mobile / Address are common values: they are
      // edited in the Organization Profile section and mirrored here so
      // invoices and other consumers of the settings API stay in sync.
      try {
        await settingsApi.updateSettings({
          companyName: orgProfile.name || "",
          companyAddress: orgProfile.address || "",
          companyState: invoiceSettings.companyState,
          companyStateCode: invoiceSettings.companyStateCode,
          gstin: invoiceSettings.gstin,
          cin: invoiceSettings.cin,
          pan: invoiceSettings.pan,
          hsnSacCode: invoiceSettings.hsnSacCode,
          phoneNumber: payload.phone,
          website: invoiceSettings.website,
          email: payload.email,
        });
      } catch (settingsErr) {
        console.error("Error saving company & GST details from organization tab:", settingsErr);
      }

      setLogoFile(null);
      setSignatureFile(null);
      setIsEditMode(false);

      // If logo was uploaded, also save it to settings API as logoBase64
      if (logoFile) {
        try {
          const logoBase64 = await fileToBase64(logoFile);
          await settingsApi.updateSettings({ logoBase64 });
          setLogoPreview(logoBase64);
          // Dispatch event to update header logo
          window.dispatchEvent(new CustomEvent("businessLogoUpdated", { detail: logoBase64 }));
        } catch (settingsErr) {
          console.error("Error saving logo to settings:", settingsErr);
        }
      }

      window.dispatchEvent(new CustomEvent("businessProfileUpdated", { detail: savedProfile }));
      toast.success("Organization profile updated successfully");
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error("Error updating organization profile:", err);
      const message = err.response?.data?.message || err.message || "Failed to update organization profile";
      setOrgError(message);
      alert(`Failed to update organization profile: ${message}`);
    }
  };

  // Save Invoice Settings
  const handleSaveInvoiceSettings = async () => {
    // Prevent saving if manual challan number is duplicate
    if (invoiceSettings.useManualChallan && manualChallanValid === false) {
      toast.error("Cannot save: Manual challan number already exists. Please use a unique number or accept the suggested number.");
      return;
    }

    try {
      // Try with minimal payload first to test if it's a payload size issue
      const minimalPayload = {
        invoicePrefix: invoiceSettings.invoicePrefix,
        companyState: invoiceSettings.companyState,
        companyStateCode: invoiceSettings.companyStateCode,
        gstin: invoiceSettings.gstin,
        cin: invoiceSettings.cin,
        pan: invoiceSettings.pan,
        hsnSacCode: invoiceSettings.hsnSacCode,
        website: invoiceSettings.website,
        challanNumberFormat: invoiceSettings.challanNumberFormat,
        challanSequence: Number(invoiceSettings.challanSequence),
        terms: invoiceSettings.terms,
        defaultFormat: invoiceSettings.defaultFormat,
        useEmailShare: invoiceSettings.useEmailShare,
        useManualChallan: invoiceSettings.useManualChallan,
        // API field is `manualChallanStart` (numeric). Sending the older
        // `manualChallanNumber` key caused the backend to ignore the value.
        manualChallanStart:
          invoiceSettings.manualChallanNumber === "" ||
          invoiceSettings.manualChallanNumber === null ||
          invoiceSettings.manualChallanNumber === undefined
            ? null
            : Number(invoiceSettings.manualChallanNumber),
        useWhatsAppShare: invoiceSettings.useWhatsAppShare,
        enableChallanImageUpload: invoiceSettings.enableChallanImageUpload,
        enableChallanGeoLocation: invoiceSettings.enableChallanGeoLocation,
        showInvoicePopupAfterChallan: invoiceSettings.showInvoicePopupAfterChallan,
        // WhatsApp Marketing Templates
        whatsappNewCustomerTemplate: invoiceSettings.whatsappNewCustomerTemplate,
        whatsappProductArrivalTemplate: invoiceSettings.whatsappProductArrivalTemplate,
        whatsappSpecialOfferTemplate: invoiceSettings.whatsappSpecialOfferTemplate,
        whatsappNewCustomerPdfUrl: invoiceSettings.whatsappNewCustomerPdfUrl,
        // Product Arrival
        whatsappProductArrivalImageUrl: invoiceSettings.whatsappProductArrivalImageUrl,
        whatsappProductArrivalCompanyName: invoiceSettings.whatsappProductArrivalCompanyName,
        // Special Offer
        whatsappSpecialOfferImageUrl: invoiceSettings.whatsappSpecialOfferImageUrl,
        whatsappSpecialOfferDiscount: invoiceSettings.whatsappSpecialOfferDiscount,
        whatsappSpecialOfferExpiryDate: invoiceSettings.whatsappSpecialOfferExpiryDate,
        whatsappSpecialOfferCompanyName: invoiceSettings.whatsappSpecialOfferCompanyName,
      };

      console.log("Sending settings payload:", minimalPayload);

      await settingsApi.updateSettings(minimalPayload);

      // If successful with minimal payload, try adding images
      if (logoFile || signatureFile) {
        let signatureBase64 = signaturePreview;
        let logoBase64 = logoPreview;

        if (signatureFile) {
          signatureBase64 = await fileToBase64(signatureFile);
        }

        if (logoFile) {
          logoBase64 = await fileToBase64(logoFile);
        }

        const fullPayload = {
          ...minimalPayload,
          signatureBase64: signatureBase64,
          logoBase64: logoBase64,
        };

        await settingsApi.updateSettings(fullPayload);

        // Handle new customer PDF upload
        if (newCustomerPdfFile) {
          const pdfBase64 = await fileToBase64(newCustomerPdfFile);
          await settingsApi.updateSettings({ whatsappNewCustomerPdfUrl: pdfBase64 });
          setNewCustomerPdfPreview(pdfBase64);
          setInvoiceSettings((prev) => ({ ...prev, whatsappNewCustomerPdfUrl: pdfBase64 }));
        }

        // Dispatch event to update header logo
        if (logoBase64) {
          window.dispatchEvent(new CustomEvent("businessLogoUpdated", { detail: logoBase64 }));
        }
      }

      toast.success("Settings updated successfully");
      setShowSuccess(true);
      setIsInvoiceEditMode(false);
      setLogoFile(null);
      setSignatureFile(null);
      setNewCustomerPdfFile(null);
      // Reload settings from backend to ensure UI reflects saved values
      loadInvoiceSettings();

      setTimeout(() => setShowSuccess(false), 3000);

    } catch (err) {
      console.error("Error updating settings:", err);
      if (err.response?.status === 401) {
        alert("Authentication failed. You may not have permission to update settings. Please contact your administrator.");
      } else {
        alert(`Failed to update settings: ${err.response?.data?.message || err.message}`);
      }
    }
  };

  // Save Banking Settings
  const handleSaveBankingSettings = async () => {
    try {
      const payload = {
        accountName: bankingSettings.accountName,
        accountNumber: bankingSettings.accountNumber,
        ifscCode: bankingSettings.ifscCode,
        branch: bankingSettings.branch,
        bankName: bankingSettings.bankName,
        upiId: bankingSettings.upiId,
      };

      if (qrImageFile) {
        payload.qrImageBase64 = await fileToBase64(qrImageFile);
      }

      console.log("Sending banking settings payload:", payload);

      const res = await settingsApi.updateSettings(payload);

      if (qrImageFile && res?.data?.qrImageBase64) {
        setQrImagePreview(res.data.qrImageBase64.startsWith('data:') ? res.data.qrImageBase64 : `data:image/jpeg;base64,${res.data.qrImageBase64}`);
      }

      toast.success("Banking settings updated successfully");
      setShowSuccess(true);
      setIsBankingEditMode(false);
      setQrImageFile(null);
      // Reload settings from backend to ensure UI reflects saved values
      loadBankingSettings();

      setTimeout(() => setShowSuccess(false), 3000);

    } catch (err) {
      console.error("Error updating banking settings:", err);
      if (err.response?.status === 401) {
        alert("Authentication failed. You may not have permission to update settings. Please contact your administrator.");
      } else {
        alert(`Failed to update banking settings: ${err.response?.data?.message || err.message}`);
      }
    }
  };

  // ===== EVENT HANDLERS =====

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // ===== JSX RENDER =====

  // Dashed upload box used by the Sharing Settings image fields.
  const renderSharingImageUpload = ({ id, heading, value, field }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" sx={{ fontWeight: 500, color: 'var(--settings-text)', fontSize: '0.7rem', mb: 1, display: 'block' }}>
        {heading}
      </Typography>
      <Box
        sx={{
          border: '2px dashed',
          borderColor: value ? 'var(--settings-primary)' : 'var(--settings-border)',
          borderRadius: 2,
          p: value ? 1 : 2,
          textAlign: 'center',
          cursor: isSharingEditMode ? 'pointer' : 'not-allowed',
          backgroundColor: value ? 'rgba(79, 70, 229, 0.04)' : 'transparent',
          transition: 'all 0.2s',
          '&:hover': isSharingEditMode
            ? { borderColor: 'var(--settings-primary)', backgroundColor: 'rgba(79, 70, 229, 0.08)' }
            : {},
        }}
        onClick={() => isSharingEditMode && document.getElementById(id).click()}
      >
        <input
          type="file"
          id={id}
          hidden
          accept="image/*"
          onChange={handleSharingImageUpload(field)}
          disabled={!isSharingEditMode}
        />
        {value ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <img
              src={value}
              alt={heading}
              style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 8 }}
            />
            <Typography variant="caption" sx={{ color: 'var(--settings-primary)', fontSize: '0.65rem', fontWeight: 500 }}>
              Click to change image
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
            <ImageIcon sx={{ fontSize: 28, color: 'var(--settings-text-secondary)' }} />
            <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.7rem', fontWeight: 500 }}>
              Upload Image
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.65rem' }}>
              400x400px recommended. Max 5MB.
            </Typography>
          </Box>
        )}
      </Box>
      {value && isSharingEditMode && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleSharingImageRemove(field);
            }}
            sx={{
              color: 'var(--settings-danger, #EF4444)',
              fontSize: '0.65rem',
              minHeight: 'auto',
              p: 0,
              '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
            }}
          >
            Remove
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <div className="settingsListContainer">
        <div className="settingsListContent">
          <Box sx={{ p: "8px 4px 36px", maxWidth: { xs: '100%', md: '1400px', lg: '1600px' }, mx: 'auto' }}>
      {/* Page Header */}
      <Box sx={{ mb: { xs: 3, md: 4 }, display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          width: { xs: 56, md: 64 },
          height: { xs: 56, md: 64 },
          background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
          borderRadius: { xs: '12px', md: '16px' },
          boxShadow: '0 4px 12px rgba(79, 70, 229, 0.15)',
        }}>
          <SettingsIcon sx={{ fontSize: { xs: 32, md: 40 }, color: '#4F46E5' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 600, color: 'var(--settings-text)', mb: 0.5 }}>
            Settings
          </Typography>
          <Typography variant="body1" sx={{ color: 'var(--settings-text-secondary)', fontSize: { xs: '0.8125rem', md: '0.875rem' } }}>
            Manage your organization settings and preferences
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ mb: { xs: 3, md: 4 }, borderBottom: '1px solid var(--settings-border)' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-scrollButtons': {
              display: 'flex',
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              backgroundColor: 'var(--settings-primary)',
            },
          }}
        >
          <Tab label="Organization" sx={{ minWidth: { xs: 'auto', sm: '170px' }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }} />
          <Tab label="Banking Details" sx={{ minWidth: { xs: 'auto', sm: '170px' }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }} />
          <Tab label="Invoice Settings" sx={{ minWidth: { xs: 'auto', sm: '170px' }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }} />
          <Tab label="Sharing Settings" sx={{ minWidth: { xs: 'auto', sm: '170px' }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }} />
          <Tab label="Branding & Documents" sx={{ minWidth: { xs: 'auto', sm: '170px' }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }} />
          <Tab label="Accounting Integration" sx={{ minWidth: { xs: 'auto', sm: '170px' }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }} />
        </Tabs>
      </Box>

      {/* ===== TAB CONTENT ===== */}
      
      {/* Organization Tab */}
      {tabValue === 0 && (
        <Box>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <Avatar sx={{ backgroundColor: 'var(--settings-primary)', color: '#ffffff', width: 56, height: 56 }}>
              <BusinessIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontSize: { xs: '1.125rem', md: '1.25rem' }, fontWeight: 600, color: 'var(--settings-text)' }}>
                Organization Profile
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.875rem' }}>
                Business details used across CTMS documents and communications.
              </Typography>
            </Box>
          </Box>

          {orgLoading ? (
            <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid var(--settings-border)' }}>
              <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'var(--settings-text-secondary)' }}>
                  Loading organization data...
                </Typography>
              </CardContent>
            </Card>
          ) : orgError ? (
            <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid var(--settings-border)' }}>
              <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'var(--settings-danger)' }}>
                  {orgError}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                border: '1px solid var(--settings-border)',
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 3, md: 4 } }}>
                  {/* Company Logo */}
                  <Box sx={{ flex: { md: '0 0 auto' }, width: { md: 'auto' }, minWidth: { md: '200px' } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-text)', mb: 2, fontSize: '0.875rem' }}>
                      Logo
                    </Typography>
                    <Box
                      sx={{
                        border: '2px dashed var(--settings-border)',
                        borderRadius: 2,
                        p: { xs: 3, md: 4 },
                        textAlign: 'center',
                        cursor: isEditMode ? 'pointer' : 'not-allowed',
                        '&:hover': isEditMode ? { borderColor: 'var(--settings-primary)', backgroundColor: 'var(--settings-inner-bg)' } : {},
                        minHeight: { xs: '160px', md: '180px' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                      }}
                      onClick={() => isEditMode && document.getElementById('org-logo-upload').click()}
                    >
                      {logoPreview ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <img 
                            src={logoPreview} 
                            alt="Logo" 
                            style={{ maxWidth: '140px', maxHeight: '140px', objectFit: 'contain' }}
                            onError={(e) => {
                              console.error("Logo image failed to load:", e);
                              console.error("Logo data URL length:", logoPreview.length);
                              console.error("Logo data URL prefix:", logoPreview.substring(0, 50));
                            }}
                            onLoad={() => {
                              console.log("Logo image loaded successfully");
                            }}
                          />
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5,
                              padding: 1.5,
                              borderRadius: 1,
                              backgroundColor: isEditMode ? 'var(--settings-inner-bg)' : 'transparent',
                              cursor: isEditMode ? 'pointer' : 'not-allowed',
                              '&:hover': isEditMode ? { backgroundColor: 'var(--settings-border)' } : {},
                            }}
                            onClick={() => isEditMode && document.getElementById('org-logo-upload').click()}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#2563EB" viewBox="0 0 16 16">
                              <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5"/>
                              <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708z"/>
                            </svg>
                            <Typography variant="caption" sx={{ color: 'var(--settings-primary)', fontSize: '0.75rem', fontWeight: 500 }}>
                              Upload
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <ImageIcon sx={{ fontSize: { xs: 40, md: 48 }, color: 'var(--settings-text-secondary)', mb: 1 }} />
                          <Typography variant="body2" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.875rem' }}>
                            Logo
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5,
                              padding: 1.5,
                              borderRadius: 1,
                              backgroundColor: isEditMode ? 'var(--settings-inner-bg)' : 'transparent',
                              cursor: isEditMode ? 'pointer' : 'not-allowed',
                              '&:hover': isEditMode ? { backgroundColor: 'var(--settings-border)' } : {},
                            }}
                            onClick={() => isEditMode && document.getElementById('org-logo-upload').click()}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#2563EB" viewBox="0 0 16 16">
                              <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5"/>
                              <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708z"/>
                            </svg>
                            <Typography variant="caption" sx={{ color: 'var(--settings-primary)', fontSize: '0.75rem', fontWeight: 500 }}>
                              Upload
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                    <input
                      type="file"
                      id="org-logo-upload"
                      hidden
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={!isEditMode}
                    />
                  </Box>

                  {/* Vertical Divider */}

                  {/* Business Details */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-text)', mb: 1.5, fontSize: '0.875rem' }}>
                          Business Name
                        </Typography>
                        {isEditMode ? (
                          <TextField
                            size="small"
                            value={orgProfile.name || ""}
                            onChange={(e) => setOrgProfile({ ...orgProfile, name: e.target.value })}
                            sx={{
                              width: '300px',
                              '& .MuiInputBase-root': {
                                height: '40px',
                              },
                            }}
                          />
                        ) : (
                          <Typography variant="body1" sx={{ color: 'var(--settings-text-body)', fontSize: '0.875rem', wordBreak: 'break-word', lineHeight: '40px' }}>
                            {orgProfile.name || ""}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-text)', mb: 1.5, fontSize: '0.875rem' }}>
                          Email
                        </Typography>
                        {isEditMode ? (
                          <TextField
                            size="small"
                            value={orgProfile.email || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setOrgProfile({ ...orgProfile, email: value });
                              setOrgFieldErrors((prev) => ({ ...prev, email: validateOrgEmailField(value) }));
                            }}
                            onBlur={() =>
                              setOrgFieldErrors((prev) => ({ ...prev, email: validateOrgEmailField(orgProfile.email) }))
                            }
                            error={!!orgFieldErrors.email}
                            helperText={orgFieldErrors.email}
                            sx={{
                              width: '300px',
                              '& .MuiInputBase-root': {
                                height: '40px',
                              },
                            }}
                          />
                        ) : (
                          <Typography variant="body1" sx={{ color: 'var(--settings-text-body)', fontSize: '0.875rem', wordBreak: 'break-word', lineHeight: '40px' }}>
                            {orgProfile.email || ""}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-text)', mb: 1.5, fontSize: '0.875rem' }}>
                          Mobile Number
                        </Typography>
                        {isEditMode ? (
                          <TextField
                            size="small"
                            value={normalizeOrgPhone(orgProfile.phone)}
                            onChange={(e) => {
                              const value = normalizeOrgPhone(e.target.value);
                              setOrgProfile({ ...orgProfile, phone: value });
                              setOrgFieldErrors((prev) => ({ ...prev, phone: validateOrgPhoneField(value) }));
                            }}
                            onBlur={() =>
                              setOrgFieldErrors((prev) => ({ ...prev, phone: validateOrgPhoneField(orgProfile.phone) }))
                            }
                            error={!!orgFieldErrors.phone}
                            helperText={orgFieldErrors.phone}
                            inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  +91
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              width: '220px',
                              '& .MuiInputBase-root': {
                                height: '40px',
                              },
                            }}
                          />
                        ) : (
                          <Typography variant="body1" sx={{ color: 'var(--settings-text-body)', fontSize: '0.875rem', wordBreak: 'break-word', lineHeight: '40px' }}>
                            {orgProfile.phone ? `+91 ${normalizeOrgPhone(orgProfile.phone)}` : ""}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-text)', mb: 1.5, fontSize: '0.875rem' }}>
                          Address
                        </Typography>
                        {isEditMode ? (
                          <TextField
                            value={orgProfile.address || ""}
                            multiline
                            rows={1}
                            maxRows={4}
                            size="small"
                            onChange={(e) => setOrgProfile({ ...orgProfile, address: e.target.value })}
                            sx={{ width: { xs: '400px', md: '100%' }, maxWidth: '400px' }}
                          />
                        ) : (
                          <Typography variant="body1" sx={{ color: 'var(--settings-text-body)', fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {orgProfile.address || ""}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>


                  {/* Column 3: Company & GST Details */}
                  <Box sx={{ flex: 1, minWidth: { md: '260px' } }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-text)', mb: 1.5, fontSize: '0.875rem' }}>
                          GSTIN
                        </Typography>
                        {isEditMode ? (
                          <TextField
                            size="small"
                            value={invoiceSettings.gstin}
                            onChange={(e) => setInvoiceSettings({ ...invoiceSettings, gstin: e.target.value })}
                            fullWidth
                            placeholder="e.g., 27AAACR2478K1A12T"
                            sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                          />
                        ) : (
                          <Typography variant="body1" sx={{ color: 'var(--settings-text-body)', fontSize: '0.875rem', wordBreak: 'break-word', lineHeight: '40px' }}>
                            {invoiceSettings.gstin || ""}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-text)', mb: 1.5, fontSize: '0.875rem' }}>
                          CIN
                        </Typography>
                        {isEditMode ? (
                          <TextField
                            size="small"
                            value={invoiceSettings.cin}
                            onChange={(e) => setInvoiceSettings({ ...invoiceSettings, cin: e.target.value })}
                            fullWidth
                            placeholder="e.g., U72200TN1992PTC023097"
                            sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                          />
                        ) : (
                          <Typography variant="body1" sx={{ color: 'var(--settings-text-body)', fontSize: '0.875rem', wordBreak: 'break-word', lineHeight: '40px' }}>
                            {invoiceSettings.cin || ""}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-text)', mb: 1.5, fontSize: '0.875rem' }}>
                          PAN
                        </Typography>
                        {isEditMode ? (
                          <TextField
                            size="small"
                            value={invoiceSettings.pan}
                            onChange={(e) => setInvoiceSettings({ ...invoiceSettings, pan: e.target.value })}
                            fullWidth
                            placeholder="e.g., AABCR4781A"
                            sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                          />
                        ) : (
                          <Typography variant="body1" sx={{ color: 'var(--settings-text-body)', fontSize: '0.875rem', wordBreak: 'break-word', lineHeight: '40px' }}>
                            {invoiceSettings.pan || ""}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-text)', mb: 1.5, fontSize: '0.875rem' }}>
                          Company State
                        </Typography>
                        {isEditMode ? (
                          <TextField
                            size="small"
                            value={invoiceSettings.companyState}
                            onChange={(e) => setInvoiceSettings({ ...invoiceSettings, companyState: e.target.value })}
                            fullWidth
                            placeholder="e.g., MAHARASHTRA"
                            sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                          />
                        ) : (
                          <Typography variant="body1" sx={{ color: 'var(--settings-text-body)', fontSize: '0.875rem', wordBreak: 'break-word', lineHeight: '40px' }}>
                            {invoiceSettings.companyState || ""}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-text)', mb: 1.5, fontSize: '0.875rem' }}>
                          State Code
                        </Typography>
                        {isEditMode ? (
                          <TextField
                            size="small"
                            value={invoiceSettings.companyStateCode}
                            onChange={(e) => setInvoiceSettings({ ...invoiceSettings, companyStateCode: e.target.value })}
                            fullWidth
                            placeholder="e.g., 27"
                            sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                          />
                        ) : (
                          <Typography variant="body1" sx={{ color: 'var(--settings-text-body)', fontSize: '0.875rem', wordBreak: 'break-word', lineHeight: '40px' }}>
                            {invoiceSettings.companyStateCode || ""}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-text)', mb: 1.5, fontSize: '0.875rem' }}>
                          Website
                        </Typography>
                        {isEditMode ? (
                          <TextField
                            size="small"
                            value={invoiceSettings.website}
                            onChange={(e) => setInvoiceSettings({ ...invoiceSettings, website: e.target.value })}
                            fullWidth
                            placeholder="e.g., www.randstad.in"
                            sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                          />
                        ) : (
                          <Typography variant="body1" sx={{ color: 'var(--settings-text-body)', fontSize: '0.875rem', wordBreak: 'break-word', lineHeight: '40px' }}>
                            {invoiceSettings.website || ""}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: { xs: 3, md: 4 }, pt: 3, borderTop: '1px solid var(--settings-border)' }}>
                  {!isEditMode ? (
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={() => {
                        setIsEditMode(true);
                        setOrgFieldErrors({
                          email: validateOrgEmailField(orgProfile.email),
                          phone: validateOrgPhoneField(orgProfile.phone),
                        });
                      }}
                      sx={{
                        backgroundColor: 'var(--settings-primary)',
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        fontSize: '0.875rem',
                        height: { xs: '40px', md: '44px' },
                        '&:hover': {
                          backgroundColor: 'var(--settings-primary-hover)',
                        },
                      }}
                    >
                      Edit Settings
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setIsEditMode(false);
                          setOrgFieldErrors({ email: "", phone: "" });
                          setLogoFile(null);
                          setSignatureFile(null);
                          loadInvoiceSettings();
                        }}
                        sx={{
                          borderRadius: 2,
                          borderColor: 'var(--settings-border)',
                          color: 'var(--settings-text-secondary)',
                          px: 3,
                          py: 1.5,
                          fontSize: '0.875rem',
                          height: { xs: '40px', md: '44px' },
                          '&:hover': {
                            borderColor: 'var(--settings-border-hover)',
                            backgroundColor: 'var(--settings-inner-bg)',
                          },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveOrganization}
                        disabled={!orgFormIsValid}
                        sx={{
                          backgroundColor: 'var(--settings-primary)',
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          fontSize: '0.875rem',
                          height: { xs: '40px', md: '44px' },
                          opacity: orgFormIsValid ? 1 : 0.5,
                          '&:hover': {
                            backgroundColor: 'var(--settings-primary-hover)',
                          },
                        }}
                      >
                        Save Changes
                      </Button>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
          
        </Box>
      )}

      {/* Banking Details Tab */}
      {tabValue === 1 && (
        <Box>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <Avatar sx={{ backgroundColor: 'var(--settings-primary)', color: '#ffffff', width: 56, height: 56 }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="currentColor"/>
                <path d="M11 7V12L17 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontSize: { xs: '1.125rem', md: '1.25rem' }, fontWeight: 600, color: 'var(--settings-text)' }}>
                Banking Details
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.875rem' }}>
                Configure bank account details and UPI QR code for payments.
              </Typography>
            </Box>
          </Box>

          {showSuccess && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                backgroundColor: 'var(--settings-inner-bg)',
                border: '1px solid var(--settings-success)',
                borderLeft: '4px solid var(--settings-success)',
                borderRadius: 1.5,
                color: 'var(--settings-success)',
                fontWeight: 600,
                textAlign: 'center',
                fontSize: '0.875rem',
              }}
            >
              Settings updated successfully!
            </Box>
          )}

          <Card
            sx={{
              borderRadius: 2,
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
              border: '1px solid var(--settings-border)',
              mb: 2,
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>

              {/* Bank Account Details Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-primary)', mb: 2, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Bank Account Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 1, fontSize: '0.875rem' }}>
                        Bank Name
                      </Typography>
                      <TextField
                        size="small"
                        value={bankingSettings.bankName}
                        onChange={(e) => setBankingSettings({ ...bankingSettings, bankName: e.target.value })}
                        disabled={!isBankingEditMode}
                        fullWidth
                        placeholder="e.g., State Bank of India"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 1, fontSize: '0.875rem' }}>
                        Branch
                      </Typography>
                      <TextField
                        size="small"
                        value={bankingSettings.branch}
                        onChange={(e) => setBankingSettings({ ...bankingSettings, branch: e.target.value })}
                        disabled={!isBankingEditMode}
                        fullWidth
                        placeholder="e.g., Main Branch"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 1, fontSize: '0.875rem' }}>
                        IFSC Code
                      </Typography>
                      <TextField
                        size="small"
                        value={bankingSettings.ifscCode}
                        onChange={(e) => setBankingSettings({ ...bankingSettings, ifscCode: e.target.value.toUpperCase() })}
                        disabled={!isBankingEditMode}
                        fullWidth
                        placeholder="e.g., SBIN0001234"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 1, fontSize: '0.875rem' }}>
                        Account Name
                      </Typography>
                      <TextField
                        size="small"
                        value={bankingSettings.accountName}
                        onChange={(e) => setBankingSettings({ ...bankingSettings, accountName: e.target.value })}
                        disabled={!isBankingEditMode}
                        fullWidth
                        placeholder="e.g., John Doe"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 1, fontSize: '0.875rem' }}>
                        Account Number
                      </Typography>
                      <TextField
                        size="small"
                        value={bankingSettings.accountNumber}
                        onChange={(e) => setBankingSettings({ ...bankingSettings, accountNumber: e.target.value })}
                        disabled={!isBankingEditMode}
                        fullWidth
                        placeholder="e.g., 1234567890"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* UPI Details Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-primary)', mb: 2, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  UPI Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 1, fontSize: '0.875rem' }}>
                        UPI ID
                      </Typography>
                      <TextField
                        size="small"
                        value={bankingSettings.upiId}
                        onChange={(e) => setBankingSettings({ ...bankingSettings, upiId: e.target.value })}
                        disabled={!isBankingEditMode}
                        fullWidth
                        placeholder="e.g., johndoe@upi"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 1, fontSize: '0.875rem' }}>
                        QR Code Image
                      </Typography>
                      <Box
                        sx={{
                          border: '2px dashed var(--settings-border)',
                          borderRadius: 1.5,
                          p: 3,
                          textAlign: 'center',
                          cursor: isBankingEditMode ? 'pointer' : 'not-allowed',
                          '&:hover': isBankingEditMode ? { borderColor: 'var(--settings-primary)', backgroundColor: 'rgba(79, 70, 229, 0.04)' } : {},
                          minHeight: '160px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        onClick={() => isBankingEditMode && document.getElementById('qr-image-upload').click()}
                      >
                        {qrImagePreview ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                            <img
                              src={qrImagePreview}
                              alt="QR Code"
                              style={{ maxWidth: '140px', maxHeight: '140px', objectFit: 'contain' }}
                            />
                            <Typography variant="caption" sx={{ color: 'var(--settings-primary)', fontSize: '0.75rem', fontWeight: 500 }}>
                              Click to change
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <ImageIcon sx={{ fontSize: 40, color: 'var(--settings-text-secondary)' }} />
                            <Typography variant="body2" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.875rem' }}>
                              Upload QR Code
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <input
                        type="file"
                        id="qr-image-upload"
                        hidden
                        accept="image/*"
                        onChange={handleQrImageUpload}
                        disabled={!isBankingEditMode}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mt: { xs: 2, md: 3 } }}>
            {!isBankingEditMode ? (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setIsBankingEditMode(true)}
                size="small"
                sx={{
                  backgroundColor: 'var(--settings-primary)',
                  borderRadius: 2,
                  height: { xs: '40px', md: '44px' },
                  px: 3,
                  py: 1.5,
                  fontSize: '0.875rem',
                  '&:hover': {
                    backgroundColor: 'var(--settings-primary-hover)',
                  },
                }}
              >
                Edit Settings
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsBankingEditMode(false);
                    setQrImageFile(null);
                    loadBankingSettings();
                  }}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    borderColor: 'var(--settings-border)',
                    color: 'var(--settings-text-secondary)',
                    height: { xs: '40px', md: '44px' },
                    px: 3,
                    py: 1.5,
                    fontSize: '0.875rem',
                    '&:hover': {
                      borderColor: 'var(--settings-border-hover)',
                      backgroundColor: 'var(--settings-inner-bg)',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveBankingSettings}
                  size="small"
                  sx={{
                    backgroundColor: 'var(--settings-primary)',
                    borderRadius: 2,
                    height: { xs: '40px', md: '44px' },
                    px: 3,
                    py: 1.5,
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: 'var(--settings-primary-hover)',
                    },
                  }}
                >
                  Save Changes
                </Button>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Invoice Settings Tab */}
      {tabValue === 2 && (
        <Box>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <Avatar sx={{ backgroundColor: 'var(--settings-primary)', color: '#ffffff', width: 56, height: 56 }}>
              <InvoiceIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontSize: { xs: '1.125rem', md: '1.25rem' }, fontWeight: 600, color: 'var(--settings-text)' }}>
                Invoice Settings
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.875rem' }}>
                Configure invoice prefixes, numbering formats, and manual challan settings.
              </Typography>
            </Box>
          </Box>

          {showSuccess && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                backgroundColor: 'var(--settings-inner-bg)',
                border: '1px solid var(--settings-success)',
                borderLeft: '4px solid var(--settings-success)',
                borderRadius: 1.5,
                color: 'var(--settings-success)',
                fontWeight: 600,
                textAlign: 'center',
                fontSize: '0.875rem',
              }}
            >
              Settings updated successfully!
            </Box>
          )}

          <Card
            sx={{
              borderRadius: 2,
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
              border: '1px solid var(--settings-border)',
              mb: 2,
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              
              {/* Invoice Numbering Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-primary)', mb: 2, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Invoice Numbering
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 1, fontSize: '0.875rem' }}>
                        Invoice Prefix
                      </Typography>
                      <TextField
                        size="small"
                        value={invoiceSettings.invoicePrefix}
                        onChange={(e) => setInvoiceSettings({ ...invoiceSettings, invoicePrefix: e.target.value })}
                        disabled={!isInvoiceEditMode}
                        fullWidth
                        placeholder="e.g., INV"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 1, fontSize: '0.875rem' }}>
                        Challan Number Format
                      </Typography>
                      <TextField
                        size="small"
                        value={invoiceSettings.challanNumberFormat}
                        onChange={(e) => setInvoiceSettings({ ...invoiceSettings, challanNumberFormat: e.target.value })}
                        disabled={!isInvoiceEditMode}
                        fullWidth
                        placeholder="e.g., YY-MM-###"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 1, fontSize: '0.875rem' }}>
                        Challan Sequence
                      </Typography>
                      <TextField
                        type="text"
                        size="small"
                        value={invoiceSettings.challanSequence}
                        onChange={(e) => setInvoiceSettings({ ...invoiceSettings, challanSequence: e.target.value })}
                        disabled={!isInvoiceEditMode}
                        fullWidth
                        placeholder="e.g., 1"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Challan Format Options Section */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-primary)', mb: 2, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Challan Format Options
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5, minHeight: '76px', backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', fontSize: '0.875rem' }}>
                          Use Default Format
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.75rem' }}>
                          Automatically generate challan numbers
                        </Typography>
                      </Box>
                      <FormControlLabel
                        sx={{
                          m: 0,
                          mr: 0,
                          position: "relative",
                          zIndex: 10,
                          pointerEvents: "auto",
                          cursor: "pointer",
                        }}
                        control={
                          <Switch
                            size="small"
                            checked={toBoolean(invoiceSettings.defaultFormat)}
                            onChange={handleInvoiceToggle("defaultFormat")}
                            disabled={!isInvoiceEditMode}
                            inputProps={{ "aria-label": "Use Default Format" }}
                          />
                        }
                        label=""
                      />
                    </Box>
                  </Grid>
                  {/* Manual challan toggle */}
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        minHeight: '76px',
                        p: 2.5,
                        backgroundColor: 'var(--settings-inner-bg)',
                        borderRadius: 2,
                        border: '1px solid var(--settings-border)',
                      }}
                    >
                      <Box sx={{ minWidth: 0, pr: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: 'var(--settings-text)', fontSize: '0.875rem' }}
                        >
                          Use Manual Challan
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.75rem' }}
                        >
                          Enter manual challan numbers
                        </Typography>
                      </Box>
                      <FormControlLabel
                        sx={{
                          m: 0,
                          flexShrink: 0,
                          position: "relative",
                          zIndex: 10,
                          pointerEvents: "auto",
                          cursor: "pointer",
                        }}
                        control={
                          <Switch
                            size="small"
                            checked={toBoolean(invoiceSettings.useManualChallan)}
                            onChange={handleInvoiceToggle("useManualChallan")}
                            disabled={!isInvoiceEditMode}
                            inputProps={{ "aria-label": "useManualChallan" }}
                          />
                        }
                        label=""
                      />
                    </Box>
                  </Grid>

                  {/* Manual challan number - kept as a separate card for clean alignment */}
                  {invoiceSettings.useManualChallan && (
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          p: 2.5,
                          backgroundColor: 'var(--settings-inner-bg)',
                          borderRadius: 2,
                          border: '1px solid var(--settings-border)',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 0.5, fontSize: '0.875rem' }}
                        >
                          Manual Challan Number
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.75rem', mb: 1, display: 'block' }}
                        >
                          Enter manual challan number for new challans
                        </Typography>
                        <div style={{ position: "relative" }}>
                          <TextField
                            size="small"
                            value={invoiceSettings.manualChallanNumber}
                            onChange={(e) => {
                              const value = e.target.value;
                              setInvoiceSettings({
                                ...invoiceSettings,
                                manualChallanNumber: value,
                              });
                              // Debounce the check
                              clearTimeout(window.manualChallanCheckTimeout);
                              window.manualChallanCheckTimeout = setTimeout(() => {
                                checkManualChallanNumberAvailability(value);
                              }, 300);
                            }}
                            disabled={!isInvoiceEditMode}
                            fullWidth
                            placeholder="Enter manual challan number"
                            InputProps={{
                              style: {
                                paddingRight: manualChallanChecking || manualChallanValid !== null ? "36px" : "0",
                                borderColor: manualChallanValid === false ? "#ef4444" : manualChallanValid === true ? "#22c55e" : "",
                              },
                            }}
                          />
                          {manualChallanChecking && (
                            <span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", color: "#6b7280", pointerEvents: "none" }}>
                              ⏳
                            </span>
                          )}
                          {manualChallanValid === true && !manualChallanChecking && (
                            <span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", color: "#22c55e", pointerEvents: "none" }}>
                              ✓
                            </span>
                          )}
                          {manualChallanValid === false && (
                            <span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", color: "#ef4444", pointerEvents: "none" }}>
                              ✗
                            </span>
                          )}
                        </div>
                        {manualChallanError && (
                          <span style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px", display: "block" }}>
                            {manualChallanError}
                          </span>
                        )}
                      </Box>
                    </Grid>
                  )}

                  {/* Show Invoice Popup After Challan */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '76px', p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Box sx={{ minWidth: 0, pr: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: 'var(--settings-text)', fontSize: '0.875rem' }}
                        >
                          Show Invoice Popup After Challan
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.75rem' }}
                        >
                          Open invoice details popup after creating a challan
                        </Typography>
                      </Box>
                      <FormControlLabel
                        sx={{
                          m: 0,
                          flexShrink: 0,
                          position: "relative",
                          zIndex: 10,
                          pointerEvents: "auto",
                          cursor: "pointer",
                        }}
                        control={
                          <Switch
                            size="small"
                            checked={toBoolean(invoiceSettings.showInvoicePopupAfterChallan)}
                            onChange={handleInvoiceToggle("showInvoicePopupAfterChallan")}
                            disabled={!isInvoiceEditMode}
                            inputProps={{ "aria-label": "showInvoicePopupAfterChallan" }}
                          />
                        }
                        label=""
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5, minHeight: '76px', backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', fontSize: '0.875rem' }}>
                          Enable Challan Image Upload
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.75rem' }}>
                          Allow uploading images/photos for challans
                        </Typography>
                      </Box>
                      <FormControlLabel
                          sx={{
                            m: 0,
                            mr: 0,
                            position: "relative",
                            zIndex: 10,
                            pointerEvents: "auto",
                            cursor: "pointer",
                          }}
                          control={
                            <Switch
                              size="small"
                              checked={toBoolean(invoiceSettings.enableChallanImageUpload)}
                              onChange={handleInvoiceToggle("enableChallanImageUpload")}
                              disabled={!isInvoiceEditMode}
                              inputProps={{"aria-label": "enableChallanImageUpload"}}
                            />
                          }
                          label=""
                        />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', fontSize: '0.875rem' }}>
                          Enable Challan Geo Location
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.75rem' }}>
                          Capture and store geo location for challans
                        </Typography>
                      </Box>
                      <FormControlLabel
                          sx={{
                            m: 0,
                            mr: 0,
                            position: "relative",
                            zIndex: 10,
                            pointerEvents: "auto",
                            cursor: "pointer",
                          }}
                          control={
                            <Switch
                              size="small"
                              checked={toBoolean(invoiceSettings.enableChallanGeoLocation)}
                              onChange={handleInvoiceToggle("enableChallanGeoLocation")}
                              disabled={!isInvoiceEditMode}
                              inputProps={{"aria-label": "enableChallanGeoLocation"}}
                            />
                          }
                          label=""
                        />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mt: { xs: 2, md: 3 } }}>
            {!isInvoiceEditMode ? (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setIsInvoiceEditMode(true)}
                size="small"
                sx={{
                  backgroundColor: 'var(--settings-primary)',
                  borderRadius: 2,
                  height: { xs: '40px', md: '44px' },
                  px: 3,
                  py: 1.5,
                  fontSize: '0.875rem',
                  '&:hover': {
                    backgroundColor: 'var(--settings-primary-hover)',
                  },
                }}
              >
                Edit Settings
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsInvoiceEditMode(false);
                    setLogoFile(null);
                    setSignatureFile(null);
                    loadInvoiceSettings();
                  }}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    borderColor: 'var(--settings-border)',
                    color: 'var(--settings-text-secondary)',
                    height: { xs: '40px', md: '44px' },
                    px: 3,
                    py: 1.5,
                    fontSize: '0.875rem',
                    '&:hover': {
                      borderColor: 'var(--settings-border-hover)',
                      backgroundColor: 'var(--settings-inner-bg)',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveInvoiceSettings}
                  size="small"
                  disabled={invoiceSettings.useManualChallan && manualChallanValid === false}
                  sx={{
                    backgroundColor: 'var(--settings-primary)',
                    borderRadius: 2,
                    height: { xs: '40px', md: '44px' },
                    px: 3,
                    py: 1.5,
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: 'var(--settings-primary-hover)',
                    },
                    opacity: invoiceSettings.useManualChallan && manualChallanValid === false ? 0.5 : 1,
                  }}
                >
                  Save Changes
                </Button>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Sharing Settings Tab */}
      {tabValue === 3 && (
        <Box>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <Avatar sx={{ backgroundColor: 'var(--settings-primary)', color: '#ffffff', width: 56, height: 56 }}>
              <ShareIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontSize: { xs: '1.125rem', md: '1.25rem' }, fontWeight: 600, color: 'var(--settings-text)' }}>
                Sharing Settings
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.875rem' }}>
                Choose how generated documents can be shared with customers.
              </Typography>
            </Box>
          </Box>

          <Card
            sx={{
              borderRadius: 2,
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
              border: '1px solid var(--settings-border)',
              mb: 2,
            }}
          >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5, minHeight: '76px', backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', fontSize: '0.875rem' }}>
                    WhatsApp Sharing
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.75rem' }}>
                    Show WhatsApp sharing actions for documents
                  </Typography>
                </Box>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={invoiceSettings.useWhatsAppShare}
                      onChange={(e) => setInvoiceSettings({ ...invoiceSettings, useWhatsAppShare: e.target.checked })}
                      disabled={!isSharingEditMode}
                      inputProps={{ "aria-label": "Use WhatsApp Sharing" }}
                    />
                  }
                  label=""
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5, minHeight: '76px', backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', fontSize: '0.875rem' }}>
                    Email Sharing
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.75rem' }}>
                    Show email sharing actions for documents
                  </Typography>
                </Box>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={invoiceSettings.useEmailShare}
                      onChange={(e) => setInvoiceSettings({ ...invoiceSettings, useEmailShare: e.target.checked })}
                      disabled={!isSharingEditMode}
                      inputProps={{ "aria-label": "Use Email Sharing" }}
                    />
                  }
                  label=""
                />
              </Box>
              
              {/* WhatsApp Marketing Templates */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-primary)', mt: 2, mb: 1, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                WhatsApp Marketing Templates
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', mb: 2, display: 'block' }}>
                Configure approved WhatsApp Business templates for automated marketing campaigns. All templates must be pre-approved in Meta Business Manager.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {/* Template Cards Grid */}
              <Grid container spacing={3}>
                
                {/* New Customer Template */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    p: 2.5, 
                    backgroundColor: 'var(--settings-inner-bg)', 
                    borderRadius: 2, 
                    border: '1px solid var(--settings-border)',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    transition: 'box-shadow 0.2s, border-color 0.2s',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      borderColor: 'var(--settings-primary)',
                    }
                  }}>
                    {/* Header with Icon */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                      <Box sx={{ 
                        p: 1, 
                        backgroundColor: 'var(--settings-primary-light)', 
                        borderRadius: 1.5,
                        color: 'var(--settings-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="17 8 12 3 7 8"></polyline>
                          <line x1="12" y1="3" x2="12" y2="15"></line>
                        </svg>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-text)', fontSize: '0.875rem', mb: 0.5 }}>
                          New Customer Onboarding
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.7rem' }}>
                          Auto-sent on lead creation
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.7rem', mb: 2, display: 'block', lineHeight: 1.4 }}>
                      Triggered automatically when a new lead is created (manual entry or IndiaMART import). Sends welcome message with combined brochure + visiting card PDF attachment.
                    </Typography>
                    
                    <Divider sx={{ mb: 2 }} />
                    
                    {/* Template Name Field */}
                    <TextField
                      size="small"
                      label="Template Name"
                      value={invoiceSettings.whatsappNewCustomerTemplate}
                      onChange={(e) => setInvoiceSettings({ ...invoiceSettings, whatsappNewCustomerTemplate: e.target.value })}
                      disabled={!isSharingEditMode}
                      fullWidth
                      placeholder="new_customer"
                      helperText="Approved template name in Meta Business Manager"
                      sx={{ mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ color: 'var(--settings-text-secondary)', mr: 1 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                              <polyline points="22,6 12,13 2,6"></polyline>
                            </svg>
                          </Box>
                        )
                      }}
                    />
                    
                    {/* PDF Upload Section */}
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: 'var(--settings-text)', fontSize: '0.7rem', mb: 1, display: 'block' }}>
                        Combined Brochure + Visiting Card PDF
                      </Typography>
                      <Box 
                        sx={{ 
                          border: '2px dashed', 
                          borderColor: newCustomerPdfPreview ? 'var(--settings-primary)' : 'var(--settings-border)',
                          borderRadius: 2, 
                          p: 2, 
                          textAlign: 'center', 
                          cursor: isSharingEditMode ? 'pointer' : 'not-allowed',
                          backgroundColor: newCustomerPdfPreview ? 'rgba(79, 70, 229, 0.04)' : 'transparent',
                          transition: 'all 0.2s',
                          '&:hover': isSharingEditMode ? { 
                            borderColor: 'var(--settings-primary)', 
                            backgroundColor: 'rgba(79, 70, 229, 0.08)' 
                          } : {},
                        }}
                        onClick={() => isSharingEditMode && document.getElementById('new-customer-pdf-upload').click()}
                      >
                        <input
                          type="file"
                          id="new-customer-pdf-upload"
                          hidden
                          accept="application/pdf"
                          onChange={handleNewCustomerPdfUpload}
                          disabled={!isSharingEditMode}
                        />
                        {newCustomerPdfPreview ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                            <Box sx={{ 
                              p: 1, 
                              backgroundColor: 'var(--settings-primary-light)', 
                              borderRadius: '50%', 
                              color: 'var(--settings-primary)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10 9 9 9 8 9"></polyline>
                              </svg>
                            </Box>
                            <Typography variant="caption" sx={{ color: 'var(--settings-primary)', fontWeight: 500, fontSize: '0.75rem', textAlign: 'center' }}>
                              PDF Ready: {newCustomerPdfFile?.name || 'Combined Brochure + Visiting Card'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.65rem', textAlign: 'center' }}>
                              {newCustomerPdfFile?.size ? (newCustomerPdfFile.size / 1024 / 1024).toFixed(2) + ' MB' : ''}
                              <Box sx={{ mt: 0.5, color: 'var(--success)', fontSize: '0.6rem', fontWeight: 500 }}>✓ Ready to send</Box>
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ 
                              p: 1.5, 
                              backgroundColor: 'rgba(239, 68, 68, 0.1)', 
                              borderRadius: '50%', 
                              color: '#EF4444',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                              </svg>
                            </Box>
                            <Typography variant="body2" sx={{ color: 'var(--settings-text-secondary)', textAlign: 'center' }}>
                              Upload Combined Brochure + Visiting Card PDF
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', textAlign: 'center', fontSize: '0.65rem' }}>
                              Single PDF containing both brochure and visiting card. Max 10MB.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <input
                        type="file"
                        id="new-customer-pdf-upload"
                        hidden
                        accept="application/pdf"
                        onChange={handleNewCustomerPdfUpload}
                        disabled={!isSharingEditMode}
                      />
                    </Box>
                    
                    {/* Badge */}
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                      <span sx={{
                        backgroundColor: newCustomerPdfPreview ? 'var(--success-light)' : 'var(--settings-inner-bg)',
                        color: newCustomerPdfPreview ? 'var(--success)' : 'var(--settings-text-secondary)',
                        border: newCustomerPdfPreview ? '1px solid var(--success)' : '1px solid var(--settings-border)',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '999px',
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>
                        {newCustomerPdfPreview ? 'PDF Attached' : 'PDF Required'}
                      </span>
                    </Box>
                  </Box>
                </Grid>
                
                {/* Product Arrival Template */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    p: 2.5, 
                    backgroundColor: 'var(--settings-inner-bg)', 
                    borderRadius: 2, 
                    border: '1px solid var(--settings-border)',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    transition: 'box-shadow 0.2s, border-color 0.2s',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      borderColor: 'var(--settings-primary)',
                    }
                  }}>
                    {/* Header with Icon */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                      <Box sx={{ 
                        p: 1, 
                        backgroundColor: 'rgba(37, 99, 235, 0.1)', 
                        borderRadius: 1.5,
                        color: '#2563EB',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                        </svg>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-text)', fontSize: '0.875rem', mb: 0.5 }}>
                          Product Arrival Alert
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.7rem' }}>
                          Campaign-based notification
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.7rem', mb: 2, display: 'block', lineHeight: 1.4 }}>
                      Send via marketing campaign when new inventory arrives. Includes product image header. Use for announcing new stock availability.
                    </Typography>
                    
                    <Divider sx={{ mb: 2 }} />
                    
                    {/* Template Name */}
                    <TextField
                      size="small"
                      label="Template Name"
                      value={invoiceSettings.whatsappProductArrivalTemplate}
                      onChange={(e) => setInvoiceSettings({ ...invoiceSettings, whatsappProductArrivalTemplate: e.target.value })}
                      disabled={!isSharingEditMode}
                      fullWidth
                      placeholder="product_arrival"
                      helperText="Approved template with image header"
                      sx={{ mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ color: 'var(--settings-text-secondary)', mr: 1 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                              <polyline points="22,6 12,13 2,6"></polyline>
                            </svg>
                          </Box>
                        )
                      }}
                    />
                    
                    {/* Product Image Upload */}
                    {renderSharingImageUpload({
                      id: 'product-arrival-image-upload',
                      heading: 'Product Image (WhatsApp Header)',
                      value: invoiceSettings.whatsappProductArrivalImageUrl || '',
                      field: 'whatsappProductArrivalImageUrl',
                    })}
                    
                    {/* Company Name for Template */}
                    <TextField
                      size="small"
                      label="Company Name (in message)"
                      value={invoiceSettings.whatsappProductArrivalCompanyName || ''}
                      onChange={(e) => setInvoiceSettings({ ...invoiceSettings, whatsappProductArrivalCompanyName: e.target.value })}
                      disabled={!isSharingEditMode}
                      fullWidth
                      placeholder="Sahil Enterprises"
                      helperText="Company name shown in the WhatsApp message footer"
                      sx={{ mb: 1 }}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ color: 'var(--settings-text-secondary)', mr: 1 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                              <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                          </Box>
                        )
                      }}
                    />
                  </Box>
                </Grid>
                
                {/* Special Offer Template */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    p: 2.5, 
                    backgroundColor: 'var(--settings-inner-bg)', 
                    borderRadius: 2, 
                    border: '1px solid var(--settings-border)',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    transition: 'box-shadow 0.2s, border-color 0.2s',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      borderColor: 'var(--settings-primary)',
                    }
                  }}>
                    {/* Header with Icon */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                      <Box sx={{ 
                        p: 1, 
                        backgroundColor: 'rgba(245, 158, 11, 0.1)', 
                        borderRadius: 1.5,
                        color: '#F59E0B',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-text)', fontSize: '0.875rem', mb: 0.5 }}>
                          Special Offer Promotion
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.7rem' }}>
                          Campaign-based promotion
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.7rem', mb: 2, display: 'block', lineHeight: 1.4 }}>
                      Send via marketing campaign for limited-time offers. Includes product image, discount %, expiry date, and CTA button. Drives urgency and conversions.
                    </Typography>
                    
                    <Divider sx={{ mb: 2 }} />
                    
                    {/* Template Name */}
                    <TextField
                      size="small"
                      label="Template Name"
                      value={invoiceSettings.whatsappSpecialOfferTemplate}
                      onChange={(e) => setInvoiceSettings({ ...invoiceSettings, whatsappSpecialOfferTemplate: e.target.value })}
                      disabled={!isSharingEditMode}
                      fullWidth
                      placeholder="special_offer"
                      helperText="Approved template with image header & URL button"
                      sx={{ mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ color: 'var(--settings-text-secondary)', mr: 1 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                              <polyline points="22,6 12,13 2,6"></polyline>
                            </svg>
                          </Box>
                        )
                      }}
                    />
                    
                    {/* Offer Image Upload */}
                    {renderSharingImageUpload({
                      id: 'special-offer-image-upload',
                      heading: 'Offer Image (WhatsApp Header)',
                      value: invoiceSettings.whatsappSpecialOfferImageUrl || '',
                      field: 'whatsappSpecialOfferImageUrl',
                    })}
                    
                    {/* Discount & Expiry Row */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ fontWeight: 500, color: 'var(--settings-text)', fontSize: '0.7rem', mb: 1, display: 'block' }}>
                          Discount
                        </Typography>
                        <TextField
                          size="small"
                          value={invoiceSettings.whatsappSpecialOfferDiscount || ''}
                          onChange={(e) => setInvoiceSettings({ ...invoiceSettings, whatsappSpecialOfferDiscount: e.target.value })}
                          disabled={!isSharingEditMode}
                          fullWidth
                          placeholder="e.g., 20% or Flat ₹500"
                          helperText="Shown in message"
                          inputProps={{ 'aria-label': 'Discount' }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ fontWeight: 500, color: 'var(--settings-text)', fontSize: '0.7rem', mb: 1, display: 'block' }}>
                          Offer Expiry Date
                        </Typography>
                        <TextField
                          size="small"
                          type="date"
                          value={invoiceSettings.whatsappSpecialOfferExpiryDate || ''}
                          onChange={(e) => setInvoiceSettings({ ...invoiceSettings, whatsappSpecialOfferExpiryDate: e.target.value })}
                          disabled={!isSharingEditMode}
                          fullWidth
                          helperText="Offer validity date"
                          inputProps={{ 'aria-label': 'Offer expiry date' }}
                          InputProps={{
                            sx: { '& input[type=date]': { minHeight: 'auto' } },
                          }}
                        />
                      </Grid>
                    </Grid>
                    
                    {/* Company Name for Template */}
                    <TextField
                      size="small"
                      label="Company Name (in message)"
                      value={invoiceSettings.whatsappSpecialOfferCompanyName || ''}
                      onChange={(e) => setInvoiceSettings({ ...invoiceSettings, whatsappSpecialOfferCompanyName: e.target.value })}
                      disabled={!isSharingEditMode}
                      fullWidth
                      placeholder="Sahil Enterprises"
                      helperText="Company name shown in the WhatsApp message footer"
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ color: 'var(--settings-text-secondary)', mr: 1 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                              <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                          </Box>
                        )
                      }}
                    />
                  </Box>
                </Grid>
                
              </Grid>
              
              {/* Template Variable Reference */}
              <Box sx={{ mt: 3, p: 2, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-primary)', mb: 1.5, fontSize: '0.875rem' }}>
                  Template Variable Reference
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.7rem', display: 'block', mb: 1.5 }}>
                  Use these variables in your Meta-approved templates. They will be replaced with actual values when sending.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 1.5, backgroundColor: 'var(--settings-bg)', borderRadius: 1, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'var(--settings-primary)', fontSize: '0.65rem', mb: 1, display: 'block', textTransform: 'uppercase' }}>New Customer ({"{"}{"{"}1{"}"}{"}"} - {"{"}{"{"}4{"}"}{"}"})</Typography>
                      <Box sx={{ fontSize: '0.7rem', color: 'var(--settings-text-secondary)', lineHeight: 2, fontFamily: 'monospace' }}>
                        {"{"}{"{"}1{"}"}{"}"} Customer Name<br/>
                        {"{"}{"{"}2{"}"}{"}"} Company Name<br/>
                        {"{"}{"{"}3{"}"}{"}"} Product / Requirement<br/>
                        {"{"}{"{"}4{"}"}{"}"} Provider (IndiaMART / Manual)
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 1.5, backgroundColor: 'var(--settings-bg)', borderRadius: 1, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#2563EB', fontSize: '0.65rem', mb: 1, display: 'block', textTransform: 'uppercase' }}>Product Arrival ({"{"}{"{"}1{"}"}{"}"} - {"{"}{"{"}3{"}"}{"}"})</Typography>
                      <Box sx={{ fontSize: '0.7rem', color: 'var(--settings-text-secondary)', lineHeight: 2, fontFamily: 'monospace' }}>
                        {"{"}{"{"}1{"}"}{"}"} Customer Name<br/>
                        {"{"}{"{"}2{"}"}{"}"} Product Name<br/>
                        {"{"}{"{"}3{"}"}{"}"} Company Name
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 1.5, backgroundColor: 'var(--settings-bg)', borderRadius: 1, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#F59E0B', fontSize: '0.65rem', mb: 1, display: 'block', textTransform: 'uppercase' }}>Special Offer ({"{"}{"{"}1{"}"}{"}"} - {"{"}{"{"}4{"}"}{"}"})</Typography>
                      <Box sx={{ fontSize: '0.7rem', color: 'var(--settings-text-secondary)', lineHeight: 2, fontFamily: 'monospace' }}>
                        {"{"}{"{"}1{"}"}{"}"} Customer Name<br/>
                        {"{"}{"{"}2{"}"}{"}"} Product Name<br/>
                        {"{"}{"{"}3{"}"}{"}"} Discount / Offer Details<br/>
                        {"{"}{"{"}4{"}"}{"}"} Expiry Date
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              
            </Box>
          </CardContent>
        </Card>
        </Box>
      )}

      {/* Action Buttons for Sharing Settings */}
      {tabValue === 3 && (
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mt: { xs: 2, md: 3 } }}>
          {!isSharingEditMode ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setIsSharingEditMode(true)}
              size="small"
              sx={{
                backgroundColor: 'var(--settings-primary)',
                borderRadius: 2,
                height: { xs: '40px', md: '44px' },
                px: 3,
                py: 1.5,
                fontSize: '0.875rem',
                '&:hover': {
                  backgroundColor: 'var(--settings-primary-hover)',
                },
              }}
            >
              Edit Settings
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setIsSharingEditMode(false);
                  loadInvoiceSettings();
                }}
                size="small"
                sx={{
                  borderRadius: 2,
                  borderColor: 'var(--settings-border)',
                  color: 'var(--settings-text-secondary)',
                  height: { xs: '40px', md: '44px' },
                  px: 3,
                  py: 1.5,
                  fontSize: '0.875rem',
                  '&:hover': {
                    borderColor: 'var(--settings-border-hover)',
                    backgroundColor: 'var(--settings-inner-bg)',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveInvoiceSettings}
                size="small"
                sx={{
                  backgroundColor: 'var(--settings-primary)',
                  borderRadius: 2,
                  height: { xs: '40px', md: '44px' },
                  px: 3,
                  py: 1.5,
                  fontSize: '0.875rem',
                  '&:hover': {
                    backgroundColor: 'var(--settings-primary-hover)',
                  },
                }}
              >
                Save Changes
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Branding & Documents Tab */}
      {tabValue === 4 && (
        <Box>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <Avatar sx={{ backgroundColor: 'var(--settings-primary)', color: '#ffffff', width: 56, height: 56 }}>
              <ImageIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontSize: { xs: '1.125rem', md: '1.25rem' }, fontWeight: 600, color: 'var(--settings-text)' }}>
                Branding & Documents
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.875rem' }}>
                Customize your company terms and signature for documents.
              </Typography>
            </Box>
          </Box>

          {/* Branding & Documents Card */}
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
              border: '1px solid var(--settings-border)',
              mb: 2,
            }}
          >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {/* Logo Section */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-text)', mb: 1.5, fontSize: '0.875rem' }}>
                  Company Logo
                </Typography>
                <Box
                  sx={{
                    border: '2px dashed var(--settings-border)',
                    borderRadius: 1.5,
                    p: { xs: 3, md: 4 },
                    textAlign: 'center',
                    cursor: isBrandingEditMode ? 'pointer' : 'not-allowed',
                    '&:hover': isBrandingEditMode ? { borderColor: 'var(--settings-primary)', backgroundColor: 'var(--settings-inner-bg)' } : {},
                    minHeight: { xs: '160px', md: '180px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                  onClick={() => isBrandingEditMode && document.getElementById('branding-logo-upload').click()}
                >
                  {logoPreview ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <img 
                        src={logoPreview} 
                        alt="Logo" 
                        style={{ maxWidth: '140px', maxHeight: '140px', objectFit: 'contain' }}
                        onError={(e) => {
                          console.error("Logo image failed to load:", e);
                        }}
                        onLoad={() => {
                          console.log("Logo image loaded successfully");
                        }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                          padding: 1.5,
                          borderRadius: 1,
                          backgroundColor: isBrandingEditMode ? 'var(--settings-inner-bg)' : 'transparent',
                          cursor: isBrandingEditMode ? 'pointer' : 'not-allowed',
                          '&:hover': isBrandingEditMode ? { backgroundColor: 'var(--settings-border)' } : {},
                        }}
                        onClick={() => isBrandingEditMode && document.getElementById('branding-logo-upload').click()}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#2563EB" viewBox="0 0 16 16">
                          <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5"/>
                          <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 1 1-.708-.708z"/>
                        </svg>
                        <Typography variant="caption" sx={{ color: 'var(--settings-primary)', fontSize: '0.75rem', fontWeight: 500 }}>
                          Upload
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <ImageIcon sx={{ fontSize: { xs: 40, md: 48 }, color: 'var(--settings-text-secondary)', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.875rem' }}>
                        Logo
                      </Typography>
                    </Box>
                  )}
                </Box>
                <input
                  type="file"
                  id="branding-logo-upload"
                  hidden
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={!isBrandingEditMode}
                />
              </Grid>

              {/* Signature Section */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-text)', mb: 1.5, fontSize: '0.875rem' }}>
                  Signature
                </Typography>
                <Box
                  sx={{
                    border: '2px dashed var(--settings-border)',
                    borderRadius: 1.5,
                    p: { xs: 3, md: 4 },
                    textAlign: 'center',
                    cursor: isBrandingEditMode ? 'pointer' : 'not-allowed',
                    '&:hover': isBrandingEditMode ? { borderColor: 'var(--settings-primary)', backgroundColor: 'var(--settings-inner-bg)' } : {},
                    minHeight: { xs: '160px', md: '180px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                  onClick={() => isBrandingEditMode && document.getElementById('signature-upload').click()}
                >
                  {signaturePreview ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <img 
                        src={signaturePreview} 
                        alt="Signature" 
                        style={{ maxWidth: '140px', maxHeight: '140px', objectFit: 'contain' }}
                        onError={(e) => {
                          console.error("Signature image failed to load:", e);
                          console.error("Signature data URL length:", signaturePreview.length);
                          console.error("Signature data URL prefix:", signaturePreview.substring(0, 50));
                        }}
                        onLoad={() => {
                          console.log("Signature image loaded successfully");
                        }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                          padding: 1.5,
                          borderRadius: 1,
                          backgroundColor: isBrandingEditMode ? 'var(--settings-inner-bg)' : 'transparent',
                          cursor: isBrandingEditMode ? 'pointer' : 'not-allowed',
                          '&:hover': isBrandingEditMode ? { backgroundColor: 'var(--settings-border)' } : {},
                        }}
                        onClick={() => isBrandingEditMode && document.getElementById('signature-upload').click()}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#2563EB" viewBox="0 0 16 16">
                          <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5"/>
                          <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708z"/>
                        </svg>
                        <Typography variant="caption" sx={{ color: 'var(--settings-primary)', fontSize: '0.75rem', fontWeight: 500 }}>
                          Upload
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <SignatureIcon sx={{ fontSize: { xs: 40, md: 48 }, color: 'var(--settings-text-secondary)', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.875rem' }}>
                        Signature
                      </Typography>
                    </Box>
                  )}
                </Box>
                <input
                  type="file"
                  id="signature-upload"
                  hidden
                  accept="image/*"
                  onChange={handleSignatureUpload}
                  disabled={!isBrandingEditMode}
                />
              </Grid>

              {/* Terms & Conditions Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-text)', mb: 1.5, fontSize: '0.875rem' }}>
                  Terms & Conditions
                </Typography>
                {isBrandingEditMode ? (
                  <TextField
                    multiline
                    rows={6}
                    size="small"
                    value={invoiceSettings.terms}
                    onChange={(e) => setInvoiceSettings({ ...invoiceSettings, terms: e.target.value })}
                    disabled={!isBrandingEditMode}
                    placeholder="Enter terms and conditions..."
                    sx={{ width: '100%' }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#334155', 
                        fontSize: '0.875rem', 
                        wordBreak: 'break-word',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {invoiceSettings.terms || ""}
                    </Typography>
                    <Button
                      variant="text"
                      onClick={() => setTermsDialogOpen(true)}
                      disabled={isBrandingEditMode}
                      sx={{
                        color: 'var(--settings-primary)',
                        fontSize: '0.875rem',
                        textTransform: 'none',
                        p: 0,
                        alignSelf: 'flex-end',
                        minWidth: 'auto',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Read More
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        </Box>
      )}

      {/* Action Buttons for Branding & Documents */}
      {tabValue === 4 && (
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mt: { xs: 2, md: 3 } }}>
          {!isBrandingEditMode ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setIsBrandingEditMode(true)}
              size="small"
              sx={{
                backgroundColor: 'var(--settings-primary)',
                borderRadius: 2,
                height: { xs: '40px', md: '44px' },
                px: 3,
                py: 1.5,
                fontSize: '0.875rem',
                '&:hover': {
                  backgroundColor: 'var(--settings-primary-hover)',
                },
              }}
            >
              Edit Settings
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setIsBrandingEditMode(false);
                  setLogoFile(null);
                  setSignatureFile(null);
                  loadInvoiceSettings();
                }}
                size="small"
                sx={{
                  borderRadius: 2,
                  borderColor: 'var(--settings-border)',
                  color: 'var(--settings-text-secondary)',
                  height: { xs: '40px', md: '44px' },
                  px: 3,
                  py: 1.5,
                  fontSize: '0.875rem',
                  '&:hover': {
                    borderColor: 'var(--settings-border-hover)',
                    backgroundColor: 'var(--settings-inner-bg)',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveInvoiceSettings}
                size="small"
                sx={{
                  backgroundColor: 'var(--settings-primary)',
                  borderRadius: 2,
                  height: { xs: '40px', md: '44px' },
                  px: 3,
                  py: 1.5,
                  fontSize: '0.875rem',
                  '&:hover': {
                    backgroundColor: 'var(--settings-primary-hover)',
                  },
                }}
              >
                Save Changes
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Terms & Conditions Dialog */}
      <Dialog
        open={termsDialogOpen}
        onClose={() => setTermsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: 'var(--settings-text)' }}>
          Terms & Conditions
        </DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={12}
            size="small"
            value={invoiceSettings.terms}
            onChange={(e) => setInvoiceSettings({ ...invoiceSettings, terms: e.target.value })}
            disabled={!isBrandingEditMode}
            placeholder="Enter terms and conditions..."
            sx={{ width: '100%' }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => setTermsDialogOpen(false)}
            sx={{
              color: 'var(--settings-text-secondary)',
              textTransform: 'none',
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Accounting Integration Tab */}
      {tabValue === 5 && (
        <Box>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <Avatar sx={{ backgroundColor: 'var(--settings-primary)', color: '#ffffff', width: 56, height: 56 }}>
              <AccountingIntegrationIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--settings-text)', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                Accounting Integration
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.875rem' }}>
                {isLeadProvider
                  ? `Configure ${accountingSettings.provider} lead integration for automatic enquiry synchronization`
                  : 'Configure Tally integration for invoice and ledger synchronization'}
              </Typography>
            </Box>
          </Box>

          <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              {/* Provider Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-primary)', mb: 2, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Integration Provider
                </Typography>
                <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                  <Select
                    size="small"
                    value={accountingSettings.provider}
                    onChange={(e) => setAccountingSettings({ ...accountingSettings, provider: e.target.value })}
                    disabled={!isAccountingEditMode && !isLeadProvider}
                    fullWidth
                    sx={{
                      backgroundColor: 'var(--settings-card-bg)',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--settings-border-hover)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#94A3B8',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--settings-primary)',
                        borderWidth: '2px',
                      },
                      '& .MuiSelect-icon': {
                        color: 'var(--settings-text-secondary)',
                      },
                    }}
                  >
                    <MenuItem value="TallyPrime">TallyPrime</MenuItem>
                    <MenuItem value="Tally">Tally</MenuItem>
                    <MenuItem value="IndiaMART">IndiaMART</MenuItem>
                    <MenuItem value="TradeIndia">TradeIndia</MenuItem>
                  </Select>
                </Box>
              </Box>

              {isLeadProvider ? (
                <LeadProviderPanel provider={accountingSettings.provider} />
              ) : (
              <>

              {/* Enable Integration Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-primary)', mb: 2, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Integration Status
                </Typography>
                <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', fontSize: '0.875rem' }}>
                        Enable Accounting Integration
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.75rem' }}>
                        Connect to Tally for invoice synchronization
                      </Typography>
                    </Box>
                    <FormControlLabel
                      sx={{
                        m: 0,
                        mr: 0,
                        position: "relative",
                        zIndex: 10,
                        pointerEvents: "auto",
                        cursor: "pointer",
                      }}
                      control={
                        <Switch
                          checked={toBoolean(accountingSettings.enabled)}
                          onChange={handleAccountingToggle}
                          disabled={!isAccountingEditMode}
                          inputProps={{ "aria-label": "Enable Accounting Integration" }}
                          size="small"
                        />
                      }
                      label=""
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: accountingConnected ? '#22C55E' : 'var(--settings-danger)' }} />
                      <Typography variant="caption" sx={{ color: 'var(--settings-text-secondary)', fontSize: '0.75rem' }}>
                        {accountingConnected ? 'Connected' : 'Disconnected'}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!isAccountingEditMode}
                      onClick={handleTestConnection}
                      sx={{
                        borderRadius: 2,
                        borderColor: 'var(--settings-primary)',
                        color: 'var(--settings-primary)',
                        fontSize: '0.75rem',
                        px: 2,
                        py: 0.5,
                        '&:hover': {
                          backgroundColor: 'var(--settings-primary)',
                        },
                      }}
                    >
                      Test Connection
                    </Button>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Connection Settings */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-primary)', mb: 2, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Connection Settings
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 1, fontSize: '0.875rem' }}>
                        Host *
                      </Typography>
                      <TextField
                        size="small"
                        value={accountingSettings.host}
                        onChange={(e) => setAccountingSettings({ ...accountingSettings, host: e.target.value })}
                        disabled={!isAccountingEditMode}
                        fullWidth
                        placeholder="localhost"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 1, fontSize: '0.875rem' }}>
                        Port *
                      </Typography>
                      <TextField
                        size="small"
                        value={accountingSettings.port}
                        onChange={(e) => setAccountingSettings({ ...accountingSettings, port: e.target.value })}
                        disabled={!isAccountingEditMode}
                        fullWidth
                        placeholder="9000"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 1, fontSize: '0.875rem' }}>
                        Company Name
                      </Typography>
                      <TextField
                        size="small"
                        value={accountingSettings.companyName}
                        onChange={(e) => setAccountingSettings({ ...accountingSettings, companyName: e.target.value })}
                        disabled={!isAccountingEditMode}
                        fullWidth
                        placeholder="Enter company name"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Invoice Settings */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-primary)', mb: 2, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Invoice Settings
                </Typography>
                <Grid container spacing={2} className="settings-accounting-invoice-grid">
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 1, fontSize: '0.875rem' }}>
                        Invoice Creation Mode *
                      </Typography>
                      <Box className="settings-accounting-radio-group" sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <FormControlLabel
                          control={
                            <Radio
                              checked={accountingSettings.invoiceCreationMode === 'Automatic'}
                              onChange={() => setAccountingSettings({ ...accountingSettings, invoiceCreationMode: 'Automatic' })}
                              disabled={!isAccountingEditMode}
                              size="small"
                            />
                          }
                          label="Automatic"
                          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'var(--settings-text)' } }}
                        />
                        <FormControlLabel
                          control={
                            <Radio
                              checked={accountingSettings.invoiceCreationMode === 'Ask'}
                              onChange={() => setAccountingSettings({ ...accountingSettings, invoiceCreationMode: 'Ask' })}
                              disabled={!isAccountingEditMode}
                              size="small"
                            />
                          }
                          label="Ask Before Creating Invoice"
                          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'var(--settings-text)' } }}
                        />
                        <FormControlLabel
                          control={
                            <Radio
                              checked={accountingSettings.invoiceCreationMode === 'Manual'}
                              onChange={() => setAccountingSettings({ ...accountingSettings, invoiceCreationMode: 'Manual' })}
                              disabled={!isAccountingEditMode}
                              size="small"
                            />
                          }
                          label="Manual"
                          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'var(--settings-text)' } }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 1, fontSize: '0.875rem' }}>
                        Voucher Type *
                      </Typography>
                      <Select
                        size="small"
                        value={accountingSettings.voucherType}
                        onChange={(e) => setAccountingSettings({ ...accountingSettings, voucherType: e.target.value })}
                        disabled={!isAccountingEditMode}
                        fullWidth
                        sx={{
                          backgroundColor: 'var(--settings-card-bg)',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--settings-border-hover)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#94A3B8',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--settings-primary)',
                            borderWidth: '2px',
                          },
                          '& .MuiSelect-icon': {
                            color: 'var(--settings-text-secondary)',
                          },
                        }}
                      >
                        <MenuItem value="Sales">Sales</MenuItem>
                        <MenuItem value="Credit Note">Credit Note</MenuItem>
                        <MenuItem value="Debit Note">Debit Note</MenuItem>
                      </Select>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 1, fontSize: '0.875rem' }}>
                        Sales Ledger *
                      </Typography>
                      <Select
                        size="small"
                        value={accountingSettings.salesLedger}
                        onChange={(e) => setAccountingSettings({ ...accountingSettings, salesLedger: e.target.value })}
                        disabled={!isAccountingEditMode}
                        fullWidth
                        sx={{
                          backgroundColor: 'var(--settings-card-bg)',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--settings-border-hover)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#94A3B8',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--settings-primary)',
                            borderWidth: '2px',
                          },
                          '& .MuiSelect-icon': {
                            color: 'var(--settings-text-secondary)',
                          },
                        }}
                      >
                        <MenuItem value="Sales Account">Sales Account</MenuItem>
                        <MenuItem value="Sales - GST">Sales - GST</MenuItem>
                        <MenuItem value="Sales - Local">Sales - Local</MenuItem>
                      </Select>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--settings-text)', mb: 1, fontSize: '0.875rem' }}>
                        Default Narration
                      </Typography>
                      <TextField
                        size="small"
                        value={accountingSettings.defaultNarration}
                        onChange={(e) => setAccountingSettings({ ...accountingSettings, defaultNarration: e.target.value })}
                        disabled={!isAccountingEditMode}
                        fullWidth
                        placeholder="Enter default narration"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Master Synchronization */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--settings-primary)', mb: 2, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Master Synchronization
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={accountingSettings.autoCreateCustomerLedger}
                            onChange={(e) => setAccountingSettings({ ...accountingSettings, autoCreateCustomerLedger: e.target.checked })}
                            disabled={!isAccountingEditMode}
                            size="small"
                          />
                        }
                        label="Auto Create Customer Ledger"
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'var(--settings-text)' } }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={accountingSettings.autoCreateStockItem}
                            onChange={(e) => setAccountingSettings({ ...accountingSettings, autoCreateStockItem: e.target.checked })}
                            disabled={!isAccountingEditMode}
                            size="small"
                          />
                        }
                        label="Auto Create Stock Item"
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'var(--settings-text)' } }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2.5, backgroundColor: 'var(--settings-inner-bg)', borderRadius: 2, border: '1px solid var(--settings-border)' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={accountingSettings.saveVoucherNumber}
                            onChange={(e) => setAccountingSettings({ ...accountingSettings, saveVoucherNumber: e.target.checked })}
                            disabled={!isAccountingEditMode}
                            size="small"
                          />
                        }
                        label="Save Tally Voucher Number in CTMS"
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'var(--settings-text)' } }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              </>
              )}

            </CardContent>
          </Card>

          {/* Action Buttons (Tally accounting settings only) */}
          {!isLeadProvider && (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: { xs: 3, md: 4 }, pt: 3, borderTop: '1px solid var(--settings-border)' }}>
            {!isAccountingEditMode ? (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setIsAccountingEditMode(true)}
                size="small"
                sx={{
                  borderRadius: 2,
                  backgroundColor: 'var(--settings-primary)',
                  color: 'white',
                  height: { xs: '40px', md: '44px' },
                  px: 3,
                  py: 1.5,
                  fontSize: '0.875rem',
                  '&:hover': {
                    backgroundColor: 'var(--settings-primary-hover)',
                  },
                }}
              >
                Edit Settings
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={() => {
                    // Accounting settings are currently kept in component state.
                    // Wire this action to the accounting-settings API when that
                    // endpoint is available.
                    setIsAccountingEditMode(false);
                  }}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    borderColor: 'var(--settings-border)',
                    color: 'var(--settings-text-secondary)',
                    height: { xs: '40px', md: '44px' },
                    px: 3,
                    py: 1.5,
                    fontSize: '0.875rem',
                    '&:hover': {
                      borderColor: 'var(--settings-border-hover)',
                      backgroundColor: 'var(--settings-inner-bg)',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => {
                    setIsAccountingEditMode(false);
                    toast.success("Accounting settings saved");
                  }}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'var(--settings-primary)',
                    color: 'white',
                    height: { xs: '40px', md: '44px' },
                    px: 3,
                    py: 1.5,
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: 'var(--settings-primary-hover)',
                    },
                  }}
                >
                  Save Settings
                </Button>
              </>
            )}
          </Box>
          )}
        </Box>
      )}
    </Box>
  </div>
</div>
</>
  );
};

export default SettingsList;